import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { BETWEEN_SALES, UpcomingEvent, notPast } from '../config/betweenSales';
import { useOpenSales, OpenSale } from '../hooks/useOpenSales';
import { deriveSaleState, saleStateLabelFor } from '../utils/saleState';

/**
 * THE HOME PAGE (always — Home.tsx no longer swaps it out).
 *
 *   lead        the OPEN sales when there are any (useOpenSales -> isSaleOpen, the sale list's own
 *               definition), otherwise the upcoming slate from config/betweenSales.ts
 *   NOW BUYING  the market boards, always, under the lead
 *   then        the upcoming slate (when a live sale took the lead), teaser, Past Auctions, contact
 *
 * Copy lives in config/betweenSales.ts. Expired entries remove themselves. Reads only what the sale
 * list already reads (auctions); renders titles, dates and lot counts — no customer data. While the
 * first read is loading, or if it fails, the page shows the no-sale layout: never blank.
 *   previewNoSale (/?between=1) shows the no-sale layout even while a sale is live.
 */
const ymdToDate = (ymd: string) => new Date(`${ymd}T12:00:00`);
const dateRange = (e: UpcomingEvent) => {
  if (e.dateLabel) return e.dateLabel;
  const en = ymdToDate(e.endDate);
  if (!e.startDate) return `Ends ${format(en, 'EEEE, MMMM d, yyyy')}`;
  const s = ymdToDate(e.startDate);
  if (e.startDate === e.endDate) return format(s, 'EEEE, MMMM d, yyyy');
  return s.getMonth() === en.getMonth()
    ? `${format(s, 'MMMM d')} – ${format(en, 'd, yyyy')}`
    : `${format(s, 'MMMM d')} – ${format(en, 'MMMM d, yyyy')}`;
};
const KIND_LABEL: Record<UpcomingEvent['kind'], string> = { 'estate-sale': 'Estate Sale', auction: 'Auction', event: 'Event' };
const mapLink = (q: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

const LiveBlock: React.FC<{ sales: OpenSale[] }> = ({ sales }) => (
  <section aria-labelledby="live" className="space-y-4">
    <h1 id="live" className="text-3xl sm:text-4xl font-bold text-gray-900 text-center pt-2">
      {sales.length === 1 ? 'Live now' : `${sales.length} sales live now`}
    </h1>
    <ul className="grid gap-4">
      {sales.map((s) => {
        const label = saleStateLabelFor(deriveSaleState(s), s);
        const next = s._soonestEndMs ? new Date(s._soonestEndMs) : null;
        return (
          <li key={s.id}>
            <Link to={`/sale/${s.id}`} className="block rounded-xl border-2 border-green-300 bg-white p-5 hover:border-green-500 hover:shadow-md transition-all">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-bold">{label.toUpperCase()}</span>
                  <h2 className="text-xl font-bold text-gray-900">{s.title}</h2>
                  <p className="text-sm text-gray-700">
                    {(s.lots || []).length} lots{next ? ` · next lot closes ${format(next, 'EEE MMM d, h:mm a')}` : ''}
                  </p>
                </div>
                <span className="btn-primary text-sm">Go to the sale →</span>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
    <p className="text-center text-sm"><Link to="/browse" className="text-primary-600 hover:underline">See every sale →</Link></p>
  </section>
);

const BetweenSales: React.FC<{ previewNoSale?: boolean }> = ({ previewNoSale = false }) => {
  const c = BETWEEN_SALES;
  const { status, open } = useOpenSales();
  const live = !previewNoSale && status === 'ready' && open.length > 0;
  const events = c.events.filter((e) => notPast(e.endDate)).sort((a, b) => (a.startDate || a.endDate).localeCompare(b.startDate || b.endDate));
  const sites = c.comingSoon.filter((s) => notPast(s.showUntil));
  const teaser = c.teaser && notPast(c.teaser.showUntil) ? c.teaser : null;
  const nb = c.nowBuying;

  const upcoming = events.length > 0 ? (
    <section aria-labelledby="upcoming" className="space-y-4">
      <h2 id="upcoming" className="text-xl font-bold text-gray-900">{live ? 'Coming up' : 'Upcoming'}</h2>
      <ul className="grid gap-5 md:grid-cols-2">
        {events.map((e) => (
          <li key={e.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col">
            {e.image && <img src={e.image} alt="" className="w-full h-52 object-cover bg-gray-100" />}
            <div className="p-5 flex flex-col gap-2 flex-1">
              <span className="self-start text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 font-semibold">{KIND_LABEL[e.kind]}</span>
              <h3 className="text-lg font-bold text-gray-900">{e.title}</h3>
              <p className="text-sm text-gray-800 font-semibold">{dateRange(e)}{e.hours ? ` · ${e.hours}` : ''}</p>
              {(e.address || e.venue) && (
                <p className="text-sm text-gray-700">
                  <a href={mapLink(e.address || e.venue!)} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    📍 {e.address || e.venue}
                  </a>
                </p>
              )}
              {e.description && <p className="text-sm text-gray-600 whitespace-pre-line">{e.description}</p>}
              {e.link && (
                <a href={e.link.url} target="_blank" rel="noopener noreferrer" className="btn-primary text-sm self-start mt-auto">{e.link.label}</a>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  ) : null;

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      {live ? <LiveBlock sales={open} /> : (
        <>
          <header className="text-center space-y-2 pt-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">{c.heading}</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">{c.intro}</p>
          </header>
          {upcoming}
        </>
      )}

      <section aria-labelledby="buying" className="rounded-xl bg-gray-900 text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 id="buying" className="text-2xl font-extrabold tracking-wide">{nb.headline}</h2>
          <p className="text-gray-200 mt-1">{nb.text}</p>
        </div>
        <a href={nb.url} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-lg bg-white text-gray-900 font-semibold px-5 py-2.5 hover:bg-gray-100 text-center">
          {nb.linkLabel} →
        </a>
      </section>

      {live && upcoming}

      {sites.length > 0 && (
        <section aria-labelledby="soon" className="space-y-4">
          <h2 id="soon" className="text-xl font-bold text-gray-900">{c.comingSoonHeading}</h2>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map((s) => {
              const body = (
                <>
                  <p className="font-bold text-gray-900">{s.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{s.tagline}</p>
                </>
              );
              return (
                <li key={s.name} className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
                  {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="block hover:opacity-80">{body}</a> : body}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {teaser && (
        <section className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900">{teaser.heading}</h2>
          <p className="text-gray-600 mt-1">{teaser.text}</p>
        </section>
      )}

      <section className="grid gap-4 sm:grid-cols-2">
        <Link to="/past" className="rounded-lg border border-gray-200 bg-white p-5 hover:border-gray-400 transition-colors">
          <p className="font-bold text-gray-900">Past Auctions →</p>
          <p className="text-sm text-gray-600 mt-1">Every sale we have closed, with what sold and the prices realized.</p>
        </Link>
        <div className="rounded-lg border border-gray-200 bg-white p-5">
          <p className="font-bold text-gray-900">Questions or consigning?</p>
          <p className="text-sm text-gray-600 mt-1">
            Call <a href={`tel:${c.phone.replace(/\D/g, '')}`} className="underline">{c.phone}</a> or email{' '}
            <a href={`mailto:${c.email}`} className="underline">{c.email}</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default BetweenSales;
